import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Separator } from '@/shared/components/ui/separator';
import type { TaskPlanDraftFormValues } from '../schemas';
import type { TaskPlanProposal } from '../types';
import { AiProposedTaskRow } from './AiProposedTaskRow';

interface AiProposedTaskListProps {
  proposal: TaskPlanProposal;
  drafts: TaskPlanDraftFormValues[];
  selectedCount: number;
  estimated: string | null;
  canRegenerate: boolean;
  onRegenerate: () => void;
  onDraftChange: (index: number, patch: Partial<TaskPlanDraftFormValues>) => void;
}

/** The proposed task list section: header, summary line and editable rows. */
export function AiProposedTaskList({
  proposal,
  drafts,
  selectedCount,
  estimated,
  canRegenerate,
  onRegenerate,
  onDraftChange,
}: AiProposedTaskListProps) {
  const { t } = useTranslation('ai');

  return (
    <>
      <Separator />
      <div className="py-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-medium text-foreground">
            {t('goalPanel.proposedCount', { count: drafts.length })}
          </p>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-sm"
            data-testid="ai-goal-regenerate"
            onClick={onRegenerate}
            disabled={!canRegenerate}
          >
            {t('goalPanel.regenerate')}
          </Button>
        </div>

        <p className="mt-1 text-xs text-muted-foreground" data-testid="ai-goal-summary">
          {t('goalPanel.selectionSummary', { count: selectedCount })}
          {estimated && ` · ${estimated}`}
        </p>

        <div className="mt-3 space-y-2">
          {drafts.map((draft, index) => (
            <AiProposedTaskRow
              key={index}
              index={index}
              title={draft.title}
              priority={draft.priority ?? null}
              estimatedMinutes={draft.estimatedMinutes ?? null}
              estimateNote={proposal.tasks[index]?.estimateNote ?? null}
              selected={draft.selected}
              titleError={draft.selected && draft.title.trim().length === 0}
              onTitleChange={(value) => onDraftChange(index, { title: value })}
              onPriorityChange={(value) => onDraftChange(index, { priority: value })}
              onEstimateChange={(value) => onDraftChange(index, { estimatedMinutes: value })}
              onSelectionChange={(selected) => onDraftChange(index, { selected })}
            />
          ))}
        </div>
      </div>
    </>
  );
}
